import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Download, Users, Wallet, HandCoins,
  TrendingUp, Loader2
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Suppress ResizeObserver errors globally
if (typeof window !== 'undefined') {
  const resizeObserverErr = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (message && message.toString().includes('ResizeObserver')) {
      return true;
    }
    return resizeObserverErr ? resizeObserverErr(message, source, lineno, colno, error) : false;
  };
}

const ReportsPage = () => {
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportType, setReportType] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await apiClient.get('/members');
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch members');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchReport = async (type) => {
    setLoading(true);
    setReportType(type);
    try {
      let response;
      switch (type) {
        case 'member':
          if (!selectedMember) {
            toast.error('Please select a member');
            setLoading(false);
            return;
          }
          response = await apiClient.get(`/reports/member-statement/${selectedMember}`);
          break;
        case 'loans':
          response = await apiClient.get('/reports/loans');
          break;
        case 'contributions':
          response = await apiClient.get('/reports/contributions');
          break;
        case 'profit':
          response = await apiClient.get('/reports/profit');
          break;
        default:
          return;
      }
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const reportElement = document.getElementById('report-content');
    if (!reportElement) return;

    toast.loading('Generating PDF...');
    
    try {
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${reportType}-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      
      toast.dismiss();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate PDF');
    }
  };

  const reportCards = [
    { 
      id: 'member', 
      title: 'Member Statement', 
      description: 'Individual member financial statement',
      icon: Users,
      color: '#0A2540'
    },
    { 
      id: 'loans', 
      title: 'Loan Report', 
      description: 'Complete loan summary and status',
      icon: Wallet,
      color: '#2962FF'
    },
    { 
      id: 'contributions', 
      title: 'Contribution Report', 
      description: 'Monthly contribution tracking',
      icon: HandCoins,
      color: '#00C853'
    },
    { 
      id: 'profit', 
      title: 'Profit Report', 
      description: 'Interest and penalty earnings',
      icon: TrendingUp,
      color: '#FF9800'
    }
  ];

  return (
    <AdminLayout title="Reports" subtitle="Generate and download financial reports">
      {/* Report Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {reportCards.map((card) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`dashboard-card p-6 cursor-pointer transition-all ${
              reportType === card.id ? 'ring-2 ring-offset-2' : ''
            }`}
            style={{ '--tw-ring-color': card.color }}
            onClick={() => {
              if (card.id === 'member' && !selectedMember) {
                toast.info('Please select a member first');
                return;
              }
              fetchReport(card.id);
            }}
            data-testid={`report-${card.id}`}
          >
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${card.color}15` }}
            >
              <card.icon className="w-6 h-6" style={{ color: card.color }} />
            </div>
            <h3 className="font-semibold text-[#0A2540] mb-1">{card.title}</h3>
            <p className="text-sm text-[#425466]">{card.description}</p>
          </motion.div>
        ))}
      </div>

      {/* Member Selection for Member Report */}
      <div className="mb-6 dashboard-card p-6">
        <h3 className="font-semibold text-[#0A2540] mb-4">Select Member for Statement</h3>
        <div className="flex gap-4">
          <select 
            value={selectedMember} 
            onChange={(e) => setSelectedMember(e.target.value)}
            className="max-w-md px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A2540] bg-white"
            data-testid="report-member-select"
          >
            <option value="">Select a member</option>
            {members.map(member => (
              <option key={member.id} value={member.id}>
                {member.name} ({member.phone})
              </option>
            ))}
          </select>
          <Button 
            onClick={() => fetchReport('member')}
            disabled={!selectedMember || loading}
            className="bg-[#0A2540] hover:bg-[#163A5F]"
            data-testid="generate-member-report-btn"
          >
            {loading && reportType === 'member' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Generate
          </Button>
        </div>
      </div>

      {/* Report Display */}
      {reportData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-semibold text-[#0A2540]">
              {reportType === 'member' ? 'Member Statement' :
               reportType === 'loans' ? 'Loan Report' :
               reportType === 'contributions' ? 'Contribution Report' : 'Profit Report'}
            </h3>
            <Button 
              onClick={downloadPDF}
              className="bg-[#00C853] hover:bg-[#009624]"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>

          <div id="report-content" className="p-8 bg-white">
            {/* Report Header */}
            <div className="text-center mb-8 pb-6 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Shri Bhavani Self Help Group
              </h1>
              <p className="text-[#425466]">Financial Report</p>
              <p className="text-sm text-[#425466] mt-2">
                Generated on: {new Date().toLocaleDateString('en-IN', { 
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </p>
            </div>

            {/* Member Report */}
            {reportType === 'member' && reportData.member && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm text-[#425466] mb-1">Member Name</h4>
                    <p className="font-semibold text-[#0A2540]">{reportData.member.name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm text-[#425466] mb-1">Phone</h4>
                    <p className="font-semibold text-[#0A2540]">{reportData.member.phone}</p>
                  </div>
                  <div>
                    <h4 className="text-sm text-[#425466] mb-1">Total Capital</h4>
                    <p className="font-semibold text-[#00C853]">{formatCurrency(reportData.member.total_capital)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm text-[#425466] mb-1">Pending Loan</h4>
                    <p className="font-semibold text-[#2962FF]">{formatCurrency(reportData.member.pending_loan)}</p>
                  </div>
                </div>

                {/* Contributions */}
                {reportData.contributions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#0A2540] mb-4">Contribution History</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F7FB]">
                        <tr>
                          <th className="px-4 py-2 text-left">Month</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                          <th className="px-4 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.contributions.map((c, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-4 py-2">{c.month}</td>
                            <td className="px-4 py-2">{formatCurrency(c.amount)}</td>
                            <td className="px-4 py-2 capitalize">{c.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Transactions */}
                {reportData.transactions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#0A2540] mb-4">Transaction History</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-[#F4F7FB]">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.transactions.slice(0, 20).map((t, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-4 py-2">{new Date(t.created_at).toLocaleDateString('en-IN')}</td>
                            <td className="px-4 py-2 capitalize">{t.type.replace('_', ' ')}</td>
                            <td className="px-4 py-2">{t.description}</td>
                            <td className="px-4 py-2">{formatCurrency(t.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Loan Report */}
            {reportType === 'loans' && reportData.summary && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="p-4 bg-[#F4F7FB] rounded-xl">
                    <p className="text-sm text-[#425466]">Total Loans</p>
                    <p className="text-2xl font-bold text-[#0A2540]">{reportData.summary.total_loans}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Active Loans</p>
                    <p className="text-2xl font-bold text-green-600">{reportData.summary.active_loans}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Total Disbursed</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(reportData.summary.total_disbursed)}</p>
                  </div>
                </div>

                <table className="w-full text-sm">
                  <thead className="bg-[#F4F7FB]">
                    <tr>
                      <th className="px-4 py-2 text-left">Member</th>
                      <th className="px-4 py-2 text-left">Amount</th>
                      <th className="px-4 py-2 text-left">Remaining</th>
                      <th className="px-4 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.loans?.map((l, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-4 py-2">{l.member_name}</td>
                        <td className="px-4 py-2">{formatCurrency(l.amount)}</td>
                        <td className="px-4 py-2">{formatCurrency(l.remaining_amount)}</td>
                        <td className="px-4 py-2 capitalize">{l.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Contribution Report */}
            {reportType === 'contributions' && reportData.summary && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Paid</p>
                    <p className="text-2xl font-bold text-green-600">{reportData.summary.paid}</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Pending</p>
                    <p className="text-2xl font-bold text-yellow-600">{reportData.summary.pending}</p>
                  </div>
                  <div className="p-4 bg-[#F4F7FB] rounded-xl">
                    <p className="text-sm text-[#425466]">Total Collected</p>
                    <p className="text-2xl font-bold text-[#0A2540]">{formatCurrency(reportData.summary.total_amount)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Profit Report */}
            {reportType === 'profit' && (
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-6">
                  <div className="p-4 bg-orange-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Total Interest</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(reportData.total_interest)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Total Penalties</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(reportData.total_penalties)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl">
                    <p className="text-sm text-[#425466]">Total Profit</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(reportData.total_profit)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-[#425466]">
              <p>This is a computer-generated report from Shri Bhavani Self Help Group Management System.</p>
            </div>
          </div>
        </motion.div>
      )}
    </AdminLayout>
  );
};

export default ReportsPage;
